<template>
  <v-dialog v-model="internalValue" max-width="440" persistent scrollable>
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header py-4">
        <v-icon color="white" class="mr-2 header-icon">mdi-lock-reset</v-icon>
        <span class="ds-dialog-title">Recuperar senha</span>
        <v-spacer />
        <v-btn icon color="white" class="close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Etapa 1: pedir o e-mail -->
      <template v-if="!sent">
        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-email-outline</v-icon>
                <span>E-mail</span>
              </div>
            </div>
            <p class="dialog-hint mb-3">
              Informe o e-mail cadastrado. Enviaremos um link para você criar uma
              nova senha.
            </p>

            <v-form ref="form" v-model="validForm" @submit.prevent="submit">
              <v-text-field
                v-model="email"
                label="E-mail"
                prepend-inner-icon="mdi-email-outline"
                outlined
                dense
                color="primary"
                autofocus
                hide-details="auto"
                :rules="[rules.required, rules.email]"
                :disabled="loading"
              />
            </v-form>

            <div v-if="errorMessage" class="auth-error-msg mt-2">
              <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
              {{ errorMessage }}
            </div>
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn
            outlined
            color="primary"
            class="ds-cancel-btn"
            :disabled="loading"
            @click="close"
          >
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            class="ds-save-btn"
            :loading="loading"
            :disabled="!validForm"
            @click="submit"
          >
            <v-icon left>mdi-send</v-icon>
            Enviar link
          </v-btn>
        </v-card-actions>
      </template>

      <!-- Etapa 2: confirmação -->
      <template v-else>
        <v-card-text class="text-center py-8">
          <v-icon size="56" color="primary" class="mb-4">
            mdi-email-check-outline
          </v-icon>
          <h3 class="sent-title mb-2">Verifique seu e-mail</h3>
          <p class="dialog-hint mb-0">
            Se houver uma conta associada a
            <strong>{{ email }}</strong
            >, o link de redefinição chegará em instantes. Ele expira em 60
            minutos.
          </p>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-spacer />
          <v-btn color="primary" depressed class="ds-save-btn" @click="close">
            Entendi
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  requestPasswordReset,
  extractPasswordError,
} from "@/services/passwordService";

export default {
  name: "ForgotPasswordDialog",

  props: {
    value: { type: Boolean, default: false },
    // E-mail já digitado na tela de login, para não fazer o usuário repetir.
    initialEmail: { type: String, default: "" },
  },

  emits: ["input"],

  data() {
    return {
      email: "",
      loading: false,
      sent: false,
      validForm: false,
      errorMessage: "",
      rules: {
        required: (v) => !!v || "Este campo é obrigatório",
        email: (v) => /.+@.+\..+/.test(v || "") || "E-mail inválido",
      },
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.email = this.initialEmail || "";
        this.sent = false;
        this.errorMessage = "";
        this.$nextTick(() => this.$refs.form?.resetValidation());
      }
    },
  },

  methods: {
    close() {
      this.internalValue = false;
    },

    async submit() {
      if (!this.$refs.form?.validate()) return;

      this.loading = true;
      this.errorMessage = "";
      try {
        await requestPasswordReset(this.email);
        this.sent = true;
      } catch (err) {
        this.errorMessage = extractPasswordError(
          err,
          "Não foi possível enviar o link. Tente novamente em instantes.",
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 8px !important;
  overflow: hidden;
}
.header-icon,
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
.dialog-hint {
  font-size: 13px;
  line-height: 1.6;
  color: var(--ds-text-soft, #607d8b);
}
.sent-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--ds-text-strong);
}
.auth-error-msg {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--ds-error, #d32f2f);
}
.ds-cancel-btn,
.ds-save-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  height: 36px !important;
  border-radius: 6px !important;
}
</style>
